import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Checkbox,
  cn,
  Label,
  Popover,
} from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
import CustomerSelectLayout from './CustomerSelectLayout';
import DivisionDetailsChips from './DivisionDetailsChips';
import EditInvoiceCriteriaModal from './EditInvoices/EditInvoiceCriteriaModal';
import EditInvoices from './EditInvoices/EditInvoices';
import { EditInvoicesProvider } from './EditInvoices/EditInvoicesContext';
import InvoiceEditLoadingModal from './EditInvoices/InvoiceEditLoadingModal';
import useInvoiceEdits, { EditError } from './EditInvoices/useInvoiceEdits';
import { useOfferBuilderContext } from './OfferBuilderContext';
import useStableGroupByBuyer from './useStableGroupByBuyer';

const CustomerSelectComponent = () => {
  const { t } = useTranslation();
  const { selectedCurrency } = useCurrencyContext();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { data: exchangeRates } = useExchangeRates();
  const { getPricingTypeDisplay } = usePricingTypeDisplay();
  const { state, dispatch, takerMarkets } = useOfferBuilderContext();
  const { error, selected, expandSelected } = state;
  const buyers = useStableGroupByBuyer({ takerMarkets });
  const hasError = error?.type === 'NO_TAKER_MARKETS_SELECTED';
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [isEditInvoicesHovered, setIsEditInvoicesHovered] = useState(false);
  const [invoiceTakerMarketIds, setInvoiceTakerMarketIds] = useState<Set<string>>(new Set());
  const invoiceTakerMarkets = takerMarkets.filter((tm) => invoiceTakerMarketIds.has(tm.id));
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkEditProgress, setBulkEditProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkEditErrors, setBulkEditErrors] = useState<EditError[]>([]);
  const { trackEnterpriseEvent } = useReporting();

  // open buyer cards that have selected taker markets only when expandSelected is true
  const defaultOpenBuyers =
    expandSelected && selected.length > 0
      ? buyers
          .filter((buyer) => buyer.takerMarkets.some((tm) => selected.includes(tm)))
          .map((buyer) => buyer.makerOrganizationUuid)
      : [];

  const { handleIncludeByCriteria } = useInvoiceEdits({
    onLoadingChange: (loading) => {
      setBulkEditLoading(loading);

      if (!loading) {
        setBulkEditProgress(null);
      }
    },
    onProgressChange: (current, total) => setBulkEditProgress({ current, total }),
    onError: (errors) => setBulkEditErrors(errors),
  });

  const handleBuyerSelect = (takerMarkets: TakerMarket[], allBuyerSelected: TakerMarket[]) => {
    const isSelecting = !takerMarkets.every((tm) => selected.includes(tm));
    // when deselecting, include all selected markets for this buyer so that any manually-selected
    // active-offer markets within the buyer are also cleared
    const payload = isSelecting ? takerMarkets : allBuyerSelected;

    trackEnterpriseEvent(isSelecting ? 'offer-builder::customer::selected' : 'offer-builder::customer::deselected', {
      selectionType: 'buyer',
      makerOrgUuid: takerMarkets[0]?.makerOrganizationUuid,
      totalTakerMarketsSelected: isSelecting
        ? selected.length + takerMarkets.length
        : selected.length - takerMarkets.length,
    });

    dispatch({ type: 'TOGGLE_TAKER_MARKETS', payload });
  };

  const handleSelectAllTakerMarkets = (takerMarkets: TakerMarket[]) => {
    const isSelecting = !allSelectableCustomersSelected;
    // when deselecting, include all currently selected markets so that any manually-selected
    // active-offer markets are also cleared, not just the selectable set
    const payload = isSelecting ? takerMarkets : selected;

    trackEnterpriseEvent(isSelecting ? 'offer-builder::customer::selected' : 'offer-builder::customer::deselected', {
      selectionType: 'select-all',
      totalTakerMarketsSelected: isSelecting ? takerMarkets.length : 0,
    });

    dispatch({ type: 'SELECT_ALL_TAKER_MARKETS', payload });
  };

  const handleTakerMarketSelect = (takerMarket: TakerMarket) => {
    const isSelecting = !selected.includes(takerMarket);

    trackEnterpriseEvent(isSelecting ? 'offer-builder::customer::selected' : 'offer-builder::customer::deselected', {
      selectionType: 'individual',
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      totalTakerMarketsSelected: isSelecting ? selected.length + 1 : selected.length - 1,
    });

    dispatch({ type: 'TOGGLE_TAKER_MARKET', payload: takerMarket });
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setTimeout(() => {
      setInvoiceTakerMarketIds(new Set());
    }, 300);
  };

  const calculateStats = (
    items: { eligibleInvoiceAmount: number; eligibleInvoiceCount: number; currency: string }[]
  ) => {
    return items.reduce<{
      eligibleInvoiceAmount: number;
      eligibleInvoiceCount: number;
    }>(
      (acc, curr) => {
        const convertedInvoiceAmount = convertCurrency({
          amount: curr.eligibleInvoiceAmount,
          exchangeRates,
          from: curr.currency,
          to: selectedCurrency,
        });

        acc.eligibleInvoiceAmount += convertedInvoiceAmount;
        acc.eligibleInvoiceCount += curr.eligibleInvoiceCount;

        return acc;
      },
      {
        eligibleInvoiceAmount: 0,
        eligibleInvoiceCount: 0,
      }
    );
  };

  const isMarketPendingWithActiveOffer = (tm: TakerMarket) =>
    tm.offerStatsStatus === 'PENDING' && tm.offerConfig.isEnabled;
  const hasExistingOffer = (tm: TakerMarket) => tm.offerConfig.isEnabled || !!tm.offerSchedule;

  const nonPendingTakerMarkets = takerMarkets.filter((tm) => !isMarketPendingWithActiveOffer(tm));
  const marketsWithoutExistingOffer = nonPendingTakerMarkets.filter((tm) => !hasExistingOffer(tm));
  // when every non-pending market has an active offer, fall back to treating all of them as selectable
  // so that Select All still works rather than doing nothing
  const selectableTakerMarkets =
    marketsWithoutExistingOffer.length > 0 ? marketsWithoutExistingOffer : nonPendingTakerMarkets;
  const allSelectableCustomersSelected =
    selectableTakerMarkets.length > 0 &&
    selected.length > 0 &&
    selectableTakerMarkets.every((tm) => selected.includes(tm));
  // fully checked when every non-pending market is selected, including any manually-selected active-offer markets
  const allCustomersSelected =
    nonPendingTakerMarkets.length > 0 && nonPendingTakerMarkets.every((tm) => selected.includes(tm));
  // indeterminate when some but not all non-pending markets are selected
  const selectAllIndeterminate = !allCustomersSelected && nonPendingTakerMarkets.some((tm) => selected.includes(tm));
  const hasSelectedCustomers = selected.length > 0;
  const hasSelectedCustomersWithInvoices = hasSelectedCustomers && selected.some((tm) => tm.totalInvoiceCount > 0);

  const editInvoicesTooltip = !hasSelectedCustomers
    ? t('builder.editInvoicesDisabledNoCustomers')
    : !hasSelectedCustomersWithInvoices
    ? t('builder.editInvoicesDisabledNoInvoices')
    : null;

  return (
    <IntercomDataTarget target="selectCustomerArea">
      <div className={cn('mb-2 text-lg font-medium', { 'text-red-700': hasError })}>
        {t('builder.selectYourCustomers')}
      </div>
      <div className="mb-4 rounded-lg bg-deepBlue-50 p-4 md:py-2">
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
          <Label className="cursor-pointer [&>span]:flex [&>span]:items-center [&>span]:gap-3" size="sm">
            <Checkbox
              checked={allCustomersSelected}
              className="h-6 w-6"
              color="secondary"
              indeterminate={selectAllIndeterminate}
              onChange={() => handleSelectAllTakerMarkets(selectableTakerMarkets)}
            />
            {t('core.selectAllCustomers')}
          </Label>
          <Popover open={isEditInvoicesHovered && !!editInvoicesTooltip}>
            <Popover.Trigger asChild>
              <span
                onMouseEnter={() => setIsEditInvoicesHovered(true)}
                onMouseLeave={() => setIsEditInvoicesHovered(false)}
              >
                <button
                  className={cn(
                    'cursor-not-allowed rounded-md border border-gray-100 bg-white px-3 py-2 text-sm font-medium text-text-primary/38 shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-lightBlue-500 focus:ring-offset-2',
                    {
                      'cursor-pointer border-transparent  text-text-primary hover:bg-black/10':
                        hasSelectedCustomersWithInvoices,
                    }
                  )}
                  data-intercom-target={'offerBuilderEditInvoicesButton' satisfies IntercomDataTargetNames}
                  disabled={!hasSelectedCustomersWithInvoices}
                  onClick={() => {
                    trackEnterpriseEvent('invoices::edit-opened', {
                      takerMarkets: selected.map((tm) => ({
                        makerOrgUuid: tm.makerOrganizationUuid,
                        marketUuid: tm.marketUuid,
                        takerUuid: tm.takerDivisionUuid,
                      })),
                      totalMarketsSelected: selected.length,
                      referrer: 'offer-builder',
                    });

                    setInvoiceTakerMarketIds(new Set(selected.map((tm) => tm.id)));
                    setShowInvoiceModal(true);
                  }}
                  type="button"
                >
                  {t('core.editInvoices')}
                </button>
              </span>
            </Popover.Trigger>
            {editInvoicesTooltip && (
              <Popover.Content arrow collisionPadding={24} side="bottom">
                <div className="max-w-52 text-center text-sm">{editInvoicesTooltip}</div>
              </Popover.Content>
            )}
          </Popover>
        </div>
      </div>
      <Accordion className="space-y-4" defaultValue={defaultOpenBuyers} type="multiple">
        {buyers.map((buyer, buyerIndex) => {
          const buyerSelectedTakerMarkets = selected.filter((takerMarket) => buyer.takerMarkets.includes(takerMarket));
          const buyerIsSelected = buyerSelectedTakerMarkets.length > 0;
          const nonPendingBuyerTakerMarkets = buyer.takerMarkets.filter((tm) => !isMarketPendingWithActiveOffer(tm));
          const buyerMarketsWithoutExistingOffer = nonPendingBuyerTakerMarkets.filter((tm) => !hasExistingOffer(tm));
          const selectableBuyerTakerMarkets =
            buyerMarketsWithoutExistingOffer.length > 0
              ? buyerMarketsWithoutExistingOffer
              : nonPendingBuyerTakerMarkets;
          const allBuyerMarketsSelected =
            nonPendingBuyerTakerMarkets.length > 0 && nonPendingBuyerTakerMarkets.every((tm) => selected.includes(tm));
          const someBuyerMarketsSelected = nonPendingBuyerTakerMarkets.some((tm) => selected.includes(tm));
          const buyerCheckboxChecked = allBuyerMarketsSelected;
          // indeterminate when some but not all non-pending markets for this buyer are selected
          const buyerCheckboxIndeterminate = !allBuyerMarketsSelected && someBuyerMarketsSelected;
          const stats = buyerIsSelected
            ? calculateStats(selected.filter((s) => s.makerOrganizationUuid === buyer.makerOrganizationUuid))
            : calculateStats(buyer.takerMarkets);

          return (
            <AccordionItem
              key={buyer.makerOrganizationUuid}
              className={cn('rounded-xl border-[0.5px] border-stroke bg-white', {
                'bg-lightBlue-500 shadow': buyerIsSelected,
                'border-red-600': hasError,
              })}
              value={buyer.makerOrganizationUuid}
            >
              <AccordionHeader asChild>
                <div className={cn('flex grow items-center gap-3 px-4', 'py-4')}>
                  <Checkbox
                    className="h-6 w-6"
                    color="secondary"
                    checked={buyerCheckboxChecked}
                    indeterminate={buyerCheckboxIndeterminate}
                    disabled={selectableBuyerTakerMarkets.length === 0}
                    onChange={() => handleBuyerSelect(selectableBuyerTakerMarkets, buyerSelectedTakerMarkets)}
                    {...(buyerIndex === 0 && {
                      'data-intercom-target': 'firstCustomerCheck' satisfies IntercomDataTargetNames,
                    })}
                  />
                  <div className="flex flex-1 flex-col gap-1 md:flex-row md:items-center md:gap-10">
                    <div>
                      <div className="font-bold">{buyer.makerOrganizationName}</div>
                      <div className="text-sm text-text-secondary">
                        {buyerIsSelected
                          ? `${asNumber({ value: buyerSelectedTakerMarkets.length })}/${t(
                              'core.divisionCountSelected',
                              {
                                count: buyer.takerMarkets.length,
                                formattedCount: asNumber({ value: buyer.takerMarkets.length }),
                              }
                            )}`
                          : t('core.divisionCount', {
                              count: buyer.takerMarkets.length,
                              formattedCount: asNumber({ value: buyer.takerMarkets.length }),
                            })}
                      </div>
                    </div>
                    <div className="md:ml-auto md:text-right">
                      <div className="text-lg font-bold">
                        {asCurrency(stats.eligibleInvoiceAmount, selectedCurrency)}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {t('core.invoiceWithCount', {
                          count: stats.eligibleInvoiceCount,
                          formattedCount: asNumber({ value: stats.eligibleInvoiceCount }),
                        })}
                      </div>
                    </div>
                  </div>
                  <AccordionTrigger
                    className={cn('ml-0', { 'hover:bg-black/10': buyerIsSelected })}
                    name={t('builder.expandCustomer')}
                    {...(buyerIndex === 0 && {
                      'data-intercom-target': 'expandFirstCustomer' satisfies IntercomDataTargetNames,
                    })}
                  />
                </div>
              </AccordionHeader>
              <AccordionContent className="px-4">
                <div className={cn('rounded-lg bg-canvas', { 'bg-lightBlue-50': buyerIsSelected })}>
                  {buyer.takerMarkets.map((takerMarket) => {
                    const isPendingWithActiveOffer = isMarketPendingWithActiveOffer(takerMarket);

                    return (
                      <div
                        key={takerMarket.id}
                        className="border-b-[0.5px] border-stroke last-of-type:border-b-transparent"
                      >
                        <div className={cn('flex gap-3 p-4', { 'opacity-38': isPendingWithActiveOffer })}>
                          <div>
                            <Checkbox
                              className="h-4 w-4"
                              checked={selected.includes(takerMarket)}
                              color="secondary"
                              disabled={isPendingWithActiveOffer}
                              onChange={() => handleTakerMarketSelect(takerMarket)}
                            />
                          </div>
                          <div className="flex flex-1 gap-3">
                            <div className="-mt-0.5 flex-1">
                              <div className="flex flex-col gap-1 md:flex-row md:gap-10">
                                <div>
                                  <div className="text-sm font-medium">{getTakerMarketName(takerMarket)}</div>
                                  <div
                                    className="text-sm font-medium"
                                    title={getTakerMarketDivisionTitle(takerMarket)?.title}
                                  >
                                    {getTakerMarketDivisionTitle(takerMarket)?.content}
                                  </div>
                                  <div className="flex items-center gap-1 text-sm text-text-secondary">
                                    <div>{getPricingTypeDisplay(takerMarket)}</div>
                                    <div className="h-[3px] w-[3px] rounded-full bg-text-secondary" />
                                    <div>{takerMarket.currency}</div>
                                  </div>
                                </div>
                                <div className="shrink-0 md:ml-auto md:text-right">
                                  <div className="font-medium">
                                    {asCurrency(takerMarket.eligibleInvoiceAmount, takerMarket.currency)}
                                  </div>
                                  <div className="text-sm text-text-secondary">
                                    {`${asNumber({
                                      value: takerMarket.eligibleInvoiceCount,
                                    })}/${t('core.invoiceWithCount', {
                                      count: takerMarket.eligibleInvoiceCount + takerMarket.takerExcludedInvoiceCount,
                                      formattedCount: asNumber({
                                        value: takerMarket.eligibleInvoiceCount + takerMarket.takerExcludedInvoiceCount,
                                      }),
                                    })}`}
                                  </div>
                                  <div className="text-sm text-text-secondary">
                                    {`${asNumber({ value: takerMarket.eligibleDpeWeightedAvg })} ${t(
                                      'core.daysPaidEarly'
                                    )}`}
                                  </div>
                                </div>
                              </div>
                              <DivisionDetailsChips takerMarket={takerMarket} />
                            </div>
                          </div>
                        </div>
                        {isPendingWithActiveOffer && (
                          <div className="px-4 pb-4">
                            <Alert description={t('builder.pendingOfferStats')} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      <EditInvoices
        buyers={buyers}
        onCloseInvoiceModal={closeInvoiceModal}
        referrer="offer-builder"
        showInvoiceModal={showInvoiceModal}
        takerMarkets={invoiceTakerMarkets}
      />
      {/* bulk takerMarket invoice edits */}
      <EditInvoiceCriteriaModal
        onClose={() => setShowBulkEditModal(false)}
        onApply={async (criteria) => {
          setShowBulkEditModal(false);
          await handleIncludeByCriteria(criteria);
        }}
        open={showBulkEditModal}
        referrer="customer-select"
        takerMarkets={selected}
      />
      <InvoiceEditLoadingModal
        open={bulkEditLoading}
        progress={bulkEditProgress}
        errors={bulkEditErrors}
        onClearErrors={() => setBulkEditErrors([])}
        onClose={() => setBulkEditLoading(false)}
      />
    </IntercomDataTarget>
  );
};

const CustomerSelect = () => {
  return (
    <EditInvoicesProvider>
      <CustomerSelectLayout>
        <CustomerSelectComponent />
      </CustomerSelectLayout>
    </EditInvoicesProvider>
  );
};

export default CustomerSelect;
